"use client"

import { contentAnalytics, type AffiliatePlacement } from "@/lib/analytics"
import type { ProductRecommendation } from "@/data/guide-product-recommendations"
import type { DirectusProduct } from "@/lib/directus"
import { AffiliatePicksCard } from "@/components/tools/affiliate-picks-card"

interface GuideProductCardProps {
  guideSlug: string
  products: ProductRecommendation[]
  /** Analytics placement. Defaults to 'inline' so existing per-section cards are unchanged. */
  placement?: AffiliatePlacement
  /** Card heading. Defaults to "Recommended Products". */
  title?: string
}

/**
 * Guide product roundup. Thin adapter over the shared AffiliatePicksCard (the
 * "Affiliate CTA — 3 items" design) so guide sections render the same premium
 * card as the tool/city surfaces — serif header, ranked rows, "Check price"
 * CTAs — instead of the old plain list.
 *
 * The static ProductRecommendation shape maps straight on: `imageUrl` -> `image`
 * (getDirectusImageUrl passes http URLs through untouched) and `affiliateUrl` ->
 * `affiliate_url`. The card owns layout, disclosure, and ranking; the eyebrow is
 * suppressed so each callsite's own title (e.g. "What you need for a Bermuda
 * lawn") stays the header without a redundant "Our picks" kicker.
 */
export function GuideProductCard({
  guideSlug,
  products,
  placement = "inline",
  title = "Recommended Products",
}: GuideProductCardProps) {
  if (products.length === 0) return null

  const items = products.map((p, i) => ({
    id: `${guideSlug}-${i}`,
    name: p.name,
    description: p.description,
    image: p.imageUrl ?? "",
    affiliate_url: p.affiliateUrl,
  })) as unknown as DirectusProduct[]

  const seeAllHref = `/products?category=${encodeURIComponent(
    products[0]?.productsPageFilter || "",
  )}&source=${encodeURIComponent(`guide-inline-${guideSlug}`)}`

  return (
    <div className="my-6">
      <AffiliatePicksCard
        title={title}
        eyebrow=""
        products={items}
        seeAllHref={seeAllHref}
        surface={`guide-${guideSlug}`}
        onProductClick={(dp) =>
          contentAnalytics.affiliateLinkClicked(guideSlug, dp.name, dp.affiliate_url, placement)
        }
      />
    </div>
  )
}
